import { useMemo } from 'react';
import { capitalizeFirstLetter, Field, FieldTypes } from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { Address } from '@/common/types';
import { lockboxReasonTitle, LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { getFullAddress } from '@/utils';

const useInboundStatusFields = () => {
  const { originalLockbox } = useLockboxDetailsStore();
  const lockboxStatus = originalLockbox?.lockboxStatus;
  const statusTitle = capitalizeFirstLetter(lockboxStatus || '');
  const reasonTitle = useMemo(() => originalLockbox?.reason
    ? lockboxReasonTitle?.[originalLockbox?.reason]
    : '', [originalLockbox?.reason]);

  const checkDetails = originalLockbox?.checkDetails;

  const checkDetailsAddress: Address = useMemo(() => ({
    line1: checkDetails?.addressLine1,
    line2: checkDetails?.addressLine2,
    city: checkDetails?.city,
    state: {
      name: checkDetails?.state,
    },
    zipcode: checkDetails?.zip,
  }), [checkDetails]);

  const checkDetailsFullAddress = useMemo(() => getFullAddress(checkDetailsAddress, true),
    [checkDetailsAddress]);

  const fields: Field[] = useMemo(() => {
    if (lockboxStatus === LockboxStatus.APPROVED
      || (lockboxStatus === LockboxStatus.COMPLETED && !originalLockbox?.checkId)) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        }
      ];
    }

    if (lockboxStatus === LockboxStatus.DECLINED
      || (lockboxStatus === LockboxStatus.COMPLETED && originalLockbox?.checkId)
    ) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'reason',
          label: 'Reason',
          value: reasonTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'check',
          label: 'Check #',
          value: originalLockbox?.checkId || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'name',
          label: 'Name',
          value: originalLockbox?.checkDetails?.vendorName || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'address',
          label: 'Address',
          value: checkDetailsFullAddress || EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.PENDING) {
      return [
        {
          type: FieldTypes.Text,
          name: 'reason',
          label: 'Reason',
          value: reasonTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    if (lockboxStatus === LockboxStatus.CANCELED) {
      return [
        {
          type: FieldTypes.Text,
          name: 'approvalStatus',
          label: 'Approval status',
          value: statusTitle || EMPTY_FIELD_VALUE,
        },
        {
          type: FieldTypes.Text,
          name: 'note',
          label: 'Note',
          value: originalLockbox?.notes || EMPTY_FIELD_VALUE,
        },
      ];
    }

    return [];
  }, [lockboxStatus, originalLockbox, statusTitle, checkDetailsFullAddress, reasonTitle]);

  return fields;
};

export default useInboundStatusFields;
